type Subscriber<T> = (val: T) => void;

let batchDepth = 0;
let pendingNotifications = new Set<() => void>();

export function batch(fn: () => void): void {
    batchDepth++;
    try {
        fn();
    } finally {
        batchDepth--;
        if (batchDepth === 0) {
            const notifications = [...pendingNotifications];
            pendingNotifications.clear();
            notifications.forEach(notify => notify());
        }
    }
}

export class Signal<T> {
    private value: T;
    private subscribers = new Set<Subscriber<T>>();

    constructor(initialValue: T) {
        this.value = initialValue;
    }

    get(): T {
        return this.value;
    }

    set(val: T): void {
        if (this.value !== val) {
            this.value = val;
            this.notify();
        }
    }

    subscribe(fn: Subscriber<T>): () => void {
        this.subscribers.add(fn);
        return () => this.subscribers.delete(fn);
    }

    private notify(): void {
        const notify = () => {
            this.subscribers.forEach(fn => fn(this.value));
        };
        
        if (batchDepth > 0) {
            pendingNotifications.add(notify);
        } else {
            notify();
        }
    }

    update(fn: (val: T) => T): void {
        this.set(fn(this.value));
    }
}

export class Computed<T> {
    private cachedValue: T;
    private signal: Signal<T>;
    private unsubscribers: (() => void)[] = [];

    constructor(private computeFn: () => T, dependencies: Signal<unknown>[]) {
        this.cachedValue = computeFn();
        this.signal = new Signal(this.cachedValue);
        
        dependencies.forEach(dep => {
            this.unsubscribers.push(dep.subscribe(() => this.recompute()));
        });
    }

    get(): T {
        return this.signal.get();
    }

    subscribe(fn: Subscriber<T>): () => void {
        return this.signal.subscribe(fn);
    }

    private recompute(): void {
        const newValue = this.computeFn();
        if (newValue !== this.cachedValue) {
            this.cachedValue = newValue;
            this.signal.set(newValue);
        }
    }

    destroy(): void {
        this.unsubscribers.forEach(unsub => unsub());
        this.unsubscribers = [];
    }
}